import { useState, useEffect } from "react"
import styled, { css } from "styled-components"
import { Info } from "@fastly/consistently-icons"
import useClickOutsideTarget from "@hooks/use-click-outside-target"

const tokens = css`
  --color-toggletip-background: var(--white);
`

const ToggletipWrapper = styled.div`
  ${tokens}
  position: relative;
  display: inline-block;
`

const HintWrapper = styled.span`
  position: absolute;
  bottom: calc(100% + 0.5rem);
  left: var(--anchor-left, auto);
  right: var(--anchor-right, auto);
  transform: var(--anchor-transform, none);
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 200px;
`

const ToggletipHint = styled.span`
  position: relative;
  display: inline-block;
  width: 100%;
  background: var(--color-toggletip-background);
  padding: ${({ theme }) => theme.space.md};
  border-radius: ${({ theme }) => theme.radii.md};
  box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.08);

  &::before {
    content: "";
    top: 100%;
    left: var(--anchor-tip-left, auto);
    right: var(--anchor-tip-right, auto);
    transform: var(--anchor-tip-transform, none);
    border-left: 15px solid transparent;
    border-right: 15px solid transparent;
    border-top: 10px solid var(--color-toggletip-background);
    height: 0;
    width: 0;
    position: absolute;
  }
`

const Button = styled.button`
  background: transparent;
  border: 0;
  padding: 12px;
`

const anchorStyles = {
  center: {
    "--anchor-left": "50%",
    "--anchor-tip-left": "50%",
    "--anchor-transform": "translateX(-50%)",
    "--anchor-tip-transform": "translateX(-50%)",
  },
  right: {
    "--anchor-right": "0",
    "--anchor-tip-right": "24px",
    "--anchor-tip-transform": "translateX(50%)",
  },
  left: {
    "--anchor-left": "0",
    "--anchor-tip-left": "24px",
    "--anchor-tip-transform": "translateX(-50%)",
  },
}

export const Toggletip = ({ children, label = "More info", anchor = "center" }) => {
  const [isHovering, setIsHovering] = useState(false)
  const [message, setMessage] = useState("")
  const [mouseLeaveTimeout, setMouseLeaveTimeout] = useState(null)
  const resetMessage = () => setMessage("")
  const applyMessage = () => setMessage(<ToggletipHint>{children}</ToggletipHint>)

  const { targetClickedRef, clickOutsideTarget, setClickOutsideTarget } = useClickOutsideTarget(false)

  const handleClick = () => setTimeout(applyMessage, 100)

  const handleMouseOver = () => {
    if (mouseLeaveTimeout) {
      clearTimeout(mouseLeaveTimeout)
      setMouseLeaveTimeout(null)
    }

    setIsHovering(true)
    setTimeout(applyMessage, 100)
  }

  const handleMouseLeave = () => {
    if (isHovering) {
      const timeout = setTimeout(resetMessage, 1000)
      setMouseLeaveTimeout(timeout)
    }

    setIsHovering(false)
  }

  const handleKeyDown = (e) => {
    if (e.keyCode === 27) {
      resetMessage()
    }
  }

  useEffect(() => {
    if (clickOutsideTarget) {
      resetMessage()
      setClickOutsideTarget(false)
    }
  }, [clickOutsideTarget, setClickOutsideTarget])

  const infoColor = message ? "blue" : "grey-90"

  return (
    <ToggletipWrapper ref={targetClickedRef} onMouseEnter={handleMouseOver} onMouseLeave={handleMouseLeave}>
      <Button type="button" aria-label={label} onClick={handleClick} onKeyDown={handleKeyDown}>
        <Info color={`var(--${infoColor})`} width="24" height="24" />
      </Button>
      <HintWrapper role="status" style={anchorStyles[anchor]}>
        {message}
      </HintWrapper>
    </ToggletipWrapper>
  )
}
